import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

/**
 * Load SVG from URL, blob URL, or raw SVG string and return 3D points along the path.
 */
export async function loadTrackPoints(svgSource, options = {}) {
  const {
    samples = 800,
    scale = 0.012,
    liftY = 8,
    flipY = true,
  } = options;

  const loader = new SVGLoader();
  const isInlineSvg =
    typeof svgSource === 'string' && svgSource.trim().startsWith('<');
  const data = isInlineSvg
    ? loader.parse(svgSource)
    : await loader.loadAsync(svgSource);

  const shapes = [];
  for (const path of data.paths) {
    const pathShapes = SVGLoader.createShapes(path);
    shapes.push(...pathShapes);
  }

  if (shapes.length === 0) {
    throw new Error('No paths found in SVG');
  }

  // Use longest shape (main circuit outline)
  let mainShape = shapes[0];
  let maxLen = 0;
  for (const shape of shapes) {
    const pts = shape.getPoints(64);
    let len = 0;
    for (let i = 1; i < pts.length; i++) {
      len += pts[i].distanceTo(pts[i - 1]);
    }
    if (len > maxLen) {
      maxLen = len;
      mainShape = shape;
    }
  }

  const flatPoints = mainShape.getPoints(samples);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of flatPoints) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const points3D = flatPoints.map((p) => {
    const x = (p.x - centerX) * scale;
    const z = flipY ? -(p.y - centerY) * scale : (p.y - centerY) * scale;
    return new THREE.Vector3(x, liftY, z);
  });

  // Close loop if endpoints are near
  if (points3D[0].distanceTo(points3D[points3D.length - 1]) > 0.05) {
    points3D.push(points3D[0].clone());
  }

  return { points: points3D, shape: mainShape };
}

export function createCurveFromPoints(points) {
  return new THREE.CatmullRomCurve3(points, true, 'centripetal', 0.2);
}

const _tangent = new THREE.Vector3();
const _lateral = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

/**
 * Parallel curve offset in the horizontal (XZ) plane — inner/outer track edges.
 */
export function createOffsetCurve(baseCurve, offsetDistance, samples = 700) {
  const points = [];

  for (let i = 0; i < samples; i++) {
    const t = samples === 1 ? 0 : i / (samples - 1);
    const p = baseCurve.getPointAt(t);
    baseCurve.getTangentAt(t, _tangent);

    _tangent.y = 0;
    if (_tangent.lengthSq() < 1e-8) {
      _tangent.set(1, 0, 0);
    } else {
      _tangent.normalize();
    }

    _lateral.crossVectors(_up, _tangent).normalize();
    points.push(
      new THREE.Vector3(
        p.x + _lateral.x * offsetDistance,
        p.y,
        p.z + _lateral.z * offsetDistance,
      ),
    );
  }

  return new THREE.CatmullRomCurve3(points, true, 'centripetal', 0.2);
}

/**
 * Vertical hologram "curtain" mesh along the path.
 */
export function buildHologramCurtain(curve, options = {}) {
  const {
    segments = 400,
    extrudeDepth = 8,
    groundY = 0,
    halfWidth = 0.06,
    material,
  } = options;

  const positions = [];
  const uvs = [];
  const indices = [];

  const up = new THREE.Vector3(0, 1, 0);
  const tangent = new THREE.Vector3();
  const right = new THREE.Vector3();
  const topA = new THREE.Vector3();
  const topB = new THREE.Vector3();
  const botA = new THREE.Vector3();
  const botB = new THREE.Vector3();

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const center = curve.getPointAt(t);
    curve.getTangentAt(t, tangent).normalize();
    right.crossVectors(tangent, up).normalize();
    if (right.lengthSq() < 0.001) right.set(1, 0, 0);

    topA.copy(center).addScaledVector(right, -halfWidth);
    topB.copy(center).addScaledVector(right, halfWidth);
    const floorY = Math.min(center.y, groundY);
    botA.copy(topA).setY(floorY);
    botB.copy(topB).setY(floorY);

    const base = positions.length / 3;

    positions.push(topA.x, topA.y, topA.z);
    positions.push(topB.x, topB.y, topB.z);
    positions.push(botA.x, botA.y, botA.z);
    positions.push(botB.x, botB.y, botB.z);

    uvs.push(t, 1, t, 1, t, 0, t, 0);

    if (i < segments) {
      const a = base;
      const b = base + 4;
      // Front face quad (two triangles)
      indices.push(a, a + 2, b);
      indices.push(a + 2, b + 2, b);
      // Back face
      indices.push(a + 1, b + 1, a + 3);
      indices.push(a + 3, b + 1, b + 3);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 1;
  return mesh;
}

/**
 * Glowing stroke along a curve (tube with configurable weight = radius).
 */
export function buildTrackTube(curve, options = {}) {
  const {
    tubularSegments = 600,
    radius = 0.035,
    color = 0xffffff,
    opacity = 0.82,
    renderOrder = 3,
    radialSegments = 6,
  } = options;

  const geometry = new THREE.TubeGeometry(
    curve,
    tubularSegments,
    radius,
    radialSegments,
    true,
  );
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = renderOrder;
  return mesh;
}

/**
 * Center + inner + outer strokes offset from the SVG centerline.
 */
export function buildCircuitStrokes(centerCurve, strokeSettings) {
  const {
    strokeSpacing,
    strokeOffsetFlip,
    showCenterStroke,
    showInnerStroke,
    showOuterStroke,
    centerStrokeColor,
    centerStrokeWeight,
    centerStrokeOpacity,
    innerStrokeColor,
    innerStrokeWeight,
    innerStrokeOpacity,
    outerStrokeColor,
    outerStrokeWeight,
    outerStrokeOpacity,
  } = strokeSettings;

  const group = new THREE.Group();
  const flip = strokeOffsetFlip ? -1 : 1;
  const spacing = strokeSpacing;

  const innerCurve = createOffsetCurve(centerCurve, -spacing * flip);
  const outerCurve = createOffsetCurve(centerCurve, spacing * flip);

  const defs = [
    {
      role: 'inner',
      enabled: showInnerStroke,
      curve: innerCurve,
      color: innerStrokeColor,
      radius: innerStrokeWeight,
      opacity: innerStrokeOpacity,
      renderOrder: 2,
    },
    {
      role: 'outer',
      enabled: showOuterStroke,
      curve: outerCurve,
      color: outerStrokeColor,
      radius: outerStrokeWeight,
      opacity: outerStrokeOpacity,
      renderOrder: 2,
    },
    {
      role: 'center',
      enabled: showCenterStroke,
      curve: centerCurve,
      color: centerStrokeColor,
      radius: centerStrokeWeight,
      opacity: centerStrokeOpacity,
      renderOrder: 3,
    },
  ];

  for (const def of defs) {
    if (!def.enabled || def.radius <= 0) continue;
    const mesh = buildTrackTube(def.curve, {
      tubularSegments: 700,
      radius: def.radius,
      color: def.color,
      opacity: def.opacity,
      renderOrder: def.renderOrder,
    });
    mesh.userData.strokeRole = def.role;
    group.add(mesh);
  }

  return group;
}

/**
 * Detect corners by curvature along sampled curve.
 */
export function detectCorners(curve, options = {}) {
  const { samples = 600, curvatureThreshold = 0.35, minSpacing = 0.04 } = options;

  const corners = [];
  let lastT = -1;

  for (let i = 0; i < samples; i++) {
    const t = i / samples;
    const t0 = Math.max(0, t - 0.008);
    const t1 = Math.min(1, t + 0.008);

    const p0 = curve.getPointAt(t0);
    const p1 = curve.getPointAt(t);
    const p2 = curve.getPointAt(t1);

    const v1 = new THREE.Vector3().subVectors(p1, p0).normalize();
    const v2 = new THREE.Vector3().subVectors(p2, p1).normalize();
    const curvature = 1 - v1.dot(v2);

    if (curvature > curvatureThreshold && t - lastT > minSpacing) {
      corners.push({ t, position: p1.clone() });
      lastT = t;
    }
  }

  return corners;
}

function cornerAdditiveMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function createCornerBeamMaterial(tint, options, beamVertexShader, beamFragmentShader) {
  const { opacity, glowStrength, glowLayer = 0 } = options;

  return new THREE.ShaderMaterial({
    vertexShader: beamVertexShader,
    fragmentShader: beamFragmentShader,
    uniforms: {
      uColor: { value: tint.clone() },
      uOpacity: { value: opacity },
      uGlowStrength: { value: glowStrength },
      uGlowLayer: { value: glowLayer },
      uTime: { value: 0 },
      uAnimSpeed: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
}

/**
 * NYC GP–style corner: target rings on track + tall glowing light pillar.
 */
export function buildCornerMarker(position, options = {}) {
  const {
    beamHeight = 16,
    ringRadius = 0.22,
    beamRadius = 0.04,
    glowRadius = 0.14,
    color = 0x3dffa8,
    ringOpacity = 0.75,
    coreOpacity = 1,
    beamOpacity = 0.38,
    glowOpacity = 0.2,
    glowStrength = 1,
    topGlowOpacity = 0,
    showBeamGlow = true,
    beamVertexShader = null,
    beamFragmentShader = null,
  } = options;

  const tint = new THREE.Color(color);
  const group = new THREE.Group();
  group.position.copy(position);

  const addPart = (mesh, part) => {
    mesh.userData.cornerPart = part;
    mesh.renderOrder = 5;
    group.add(mesh);
    return mesh;
  };

  // Bright center dot
  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(ringRadius * 0.2, 28),
    cornerAdditiveMaterial(tint, coreOpacity),
  );
  dot.rotation.x = -Math.PI / 2;
  addPart(dot, 'centerDot');

  // Inner target ring
  const innerRing = new THREE.Mesh(
    new THREE.RingGeometry(ringRadius * 0.42, ringRadius * 0.62, 40),
    cornerAdditiveMaterial(tint, ringOpacity * 0.95),
  );
  innerRing.rotation.x = -Math.PI / 2;
  addPart(innerRing, 'innerRing');

  // Outer halo ring
  const outerRing = new THREE.Mesh(
    new THREE.RingGeometry(ringRadius * 0.68, ringRadius * 0.98, 48),
    cornerAdditiveMaterial(tint, ringOpacity * 0.42),
  );
  outerRing.rotation.x = -Math.PI / 2;
  addPart(outerRing, 'outerRing');

  // Volumetric beam (shader gradient)
  const beamGeo = new THREE.CylinderGeometry(
    beamRadius * 0.35,
    beamRadius * 1.15,
    1,
    20,
    20,
    true,
  );
  const beamMat = createCornerBeamMaterial(
    tint,
    { opacity: beamOpacity, glowStrength, glowLayer: 0 },
    beamVertexShader,
    beamFragmentShader,
  );
  const beamCore = new THREE.Mesh(beamGeo, beamMat);
  beamCore.scale.y = beamHeight;
  beamCore.position.y = beamHeight / 2;
  addPart(beamCore, 'beamCore');

  if (showBeamGlow) {
    const glowGeo = new THREE.CylinderGeometry(
      glowRadius * 0.25,
      glowRadius,
      1,
      18,
      14,
      true,
    );
    const glowMat = createCornerBeamMaterial(
      tint,
      { opacity: glowOpacity, glowStrength: glowStrength * 0.85, glowLayer: 1 },
      beamVertexShader,
      beamFragmentShader,
    );
    const beamGlow = new THREE.Mesh(glowGeo, glowMat);
    beamGlow.scale.y = beamHeight * 1.02;
    beamGlow.position.y = (beamHeight * 1.02) / 2;
    addPart(beamGlow, 'beamGlow');
  }

  // Subtle top wisp (kept faint so the beam can fade out cleanly)
  const topFlare = new THREE.Mesh(
    new THREE.SphereGeometry(beamRadius * 0.65, 10, 10),
    cornerAdditiveMaterial(tint, topGlowOpacity),
  );
  topFlare.position.y = beamHeight * 0.92;
  addPart(topFlare, 'topFlare');

  // Soft ground pool under marker
  const groundPool = new THREE.Mesh(
    new THREE.CircleGeometry(ringRadius * 1.1, 32),
    cornerAdditiveMaterial(tint, glowOpacity * 0.65),
  );
  groundPool.rotation.x = -Math.PI / 2;
  groundPool.position.y = -0.02;
  addPart(groundPool, 'groundPool');

  return group;
}

/**
 * Stylized city wireframe floor for context.
 */
export function buildCityFloor(size = 14, divisions = 40) {
  const group = new THREE.Group();

  const grid = new THREE.GridHelper(size, divisions, 0x142030, 0x060a10);
  grid.position.y = 0;
  grid.material.opacity = 0.18;
  grid.material.transparent = true;
  group.add(grid);

  const blockCount = 120;
  const rng = (seed) => {
    const x = Math.sin(seed * 127.1) * 43758.5453;
    return x - Math.floor(x);
  };

  const boxGeo = new THREE.BoxGeometry(1, 1, 1);
  const edgesGeo = new THREE.EdgesGeometry(boxGeo);
  const lineMat = new THREE.LineBasicMaterial({
    color: 0x1a2a40,
    transparent: true,
    opacity: 0.14,
  });

  for (let i = 0; i < blockCount; i++) {
    const rx = (rng(i) - 0.5) * size * 0.85;
    const rz = (rng(i + 50) - 0.5) * size * 0.85;
    // Central park void
    if (Math.abs(rx) < 1.8 && Math.abs(rz) < 2.2) continue;

    const w = 0.15 + rng(i + 100) * 0.35;
    const d = 0.15 + rng(i + 200) * 0.35;
    const h = 0.1 + rng(i + 300) * 0.8;

    const edges = new THREE.LineSegments(edgesGeo, lineMat);
    edges.position.set(rx, h / 2, rz);
    edges.scale.set(w, h, d);
    group.add(edges);
  }

  return group;
}
